import { useLayoutEffect, useRef, useCallback } from 'react';
import Lenis from 'lenis';

export const ScrollStackItem = ({ children, itemClassName = '' }) => (
  <div
    className={`scroll-stack-card relative w-full h-80 my-8 p-12 rounded-[40px] shadow-[0_0_30px_rgba(0,0,0,0.1)] box-border origin-top ${itemClassName}`.trim()}
    style={{
      backfaceVisibility: 'hidden',
      WebkitBackfaceVisibility: 'hidden',
      transformStyle: 'preserve-3d',
      willChange: 'transform' // Avisa o navegador para usar a GPU
    }}
  >
    {children}
  </div>
);

const ScrollStack = ({
  children,
  className = '',
  itemDistance = 100,
  itemScale = 0.03,
  itemStackDistance = 30,
  stackPosition = '20%',
  scaleEndPosition = '10%',
  baseScale = 0.85,
  scaleDuration = 0.5,
  rotationAmount = 0,
  blurAmount = 0,
  useWindowScroll = false,
  onStackComplete
}) => {
  const scrollerRef = useRef(null);
  const stackCompletedRef = useRef(false);
  const animationFrameRef = useRef(null);
  const lenisRef = useRef(null);
  const cardsRef = useRef([]);
  const lastTransformsRef = useRef(new Map());
  const isUpdatingRef = useRef(false);

  const updateCardTransforms = useCallback(() => {
    if (!scrollerRef.current || cardsRef.current.length === 0) return;

    const scrollerRect = scrollerRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    // Define o ponto de ativação baseado na porcentagem da tela
    const stackPoint = windowHeight * (parseFloat(stackPosition) / 100);
    const endPoint = windowHeight * (parseFloat(scaleEndPosition) / 100);
    const range = stackPoint - endPoint;

    cardsRef.current.forEach((card, index) => {
      if (!card) return;
      const rect = card.getBoundingClientRect();
      const cardTop = rect.top;

      if (cardTop <= stackPoint) {
        // Cálculo de progresso com precisão decimal para evitar pulos
        const rawProgress = (stackPoint - cardTop) / range;
        const progress = Math.min(Math.max(rawProgress, 0), 1);

        const activeCardsAhead = cardsRef.current
          .slice(index + 1)
          .filter(c => c && c.getBoundingClientRect().top <= stackPoint).length;

        const activeProgress = progress;

        // Uso de fixed(4) para sub-pixels suaves e translate3d para GPU
        const translateY = (activeProgress * -itemStackDistance).toFixed(2);
        const scale = (baseScale + (1 - baseScale) * (1 - activeProgress) - (activeCardsAhead * itemScale)).toFixed(4);
        const opacity = 1;

        card.style.transform = `translate3d(0, ${translateY}px, 0) scale(${scale})`;
        card.style.opacity = opacity;
        card.style.zIndex = index;
      } else {
        card.style.transform = `translate3d(0, 0, 0) scale(1)`;
        card.style.opacity = '1';
      }
    });

    isUpdatingRef.current = false;
  }, [baseScale, itemScale, itemStackDistance, scaleEndPosition, stackPosition]);

  const setupLenis = useCallback(() => {
    const lenis = new Lenis({
      duration: 1.2,
      lerp: 0.1, // Aumentado para 0.1 para resposta mais firme
      wheelMultiplier: 1,
      smoothWheel: true,
      touchMultiplier: 1.5,
      infinite: false,
    });

    lenisRef.current = lenis;

    const scrollHandler = () => {
      if (!isUpdatingRef.current) {
        isUpdatingRef.current = true;
        animationFrameRef.current = requestAnimationFrame(updateCardTransforms);
      }
    };

    lenis.on('scroll', scrollHandler);

    const raf = (time) => {
      lenis.raf(time);
      animationFrameRef.current = requestAnimationFrame(raf);
    };

    animationFrameRef.current = requestAnimationFrame(raf);
    return lenis;
  }, [updateCardTransforms]);

  useLayoutEffect(() => {
    const lenis = setupLenis();

    // Inicializa a lista de cards
    const cardElements = scrollerRef.current.querySelectorAll('.scroll-stack-card');
    cardsRef.current = Array.from(cardElements);

    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (lenis) lenis.destroy();
      cardsRef.current = [];
    };
  }, [setupLenis]);

  const containerStyles = {
    overscrollBehavior: 'contain',
    WebkitOverflowScrolling: 'touch',
    WebkitTransform: 'translateZ(0)',
    transform: 'translateZ(0)',
    willChange: useWindowScroll ? 'auto' : 'scroll-position'
  };

  const containerClassName = useWindowScroll
    ? `relative w-full ${className}`.trim()
    : `relative w-full h-full overflow-y-auto overflow-x-visible ${className}`.trim();

  return (
    <div className={containerClassName} ref={scrollerRef} style={containerStyles}>
      <div className="scroll-stack-content w-full relative">
        {children}
      </div>
    </div>
  );
};

export default ScrollStack;